import {
  Drawer,
  Typography,
  Box,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { X, ForkKnifeIcon, Copy } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import { useDashboard } from '../../hooks/useDashboard';
import { useUser } from '../../hooks/useUser';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import React from 'react';

interface MenuDrawerProps {
  open: boolean;
  onClose: () => void;
}

const MenuDrawer = ({ open, onClose }: MenuDrawerProps) => {
  const [menuData, setMenuData] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldLoadData, setShouldLoadData] = useState(false);
  const { selectedAssistant } = useDashboard();
  const { user } = useUser();
  const { copied, copy } = useCopyToClipboard();

  // Construct dynamic S3 URL based on user data and selected assistant
  const dynamicS3Url = React.useMemo(() => {
    const tenantName = user?.tenantName;
    const storeId = selectedAssistant?.config?.knowledgeBase?.[0]?.store?.id;

    if (!tenantName || !storeId) {
      return null; // Cannot construct URL without required data
    }

    return `s3://quibble-client/pos-menu-transformed/${tenantName}/${storeId}/menu.txt`;
  }, [user?.tenantName, selectedAssistant?.config?.knowledgeBase]);

  // Convert S3 URL to HTTPS URL
  const getHttpsUrl = (s3Url: string): string => {
    const regex = new RegExp('s3://([^/]+)/(.+)');
    const match = regex.exec(s3Url);
    if (match) {
      const [, bucket, key] = match;
      return `https://${bucket}.s3.amazonaws.com/${key}`;
    }
    return s3Url;
  };

  const fetchMenuData = async () => {
    if (!shouldLoadData || !dynamicS3Url) return;

    setIsLoading(true);
    setError(null);

    try {
      const httpsUrl = getHttpsUrl(dynamicS3Url);
      const response = await fetch(`${httpsUrl}?t=${Date.now()}`, {
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch: ${response.status} ${response.statusText}`
        );
      }

      const textData = await response.text();
      setMenuData(textData);
    } catch (err) {
      console.error('Error fetching menu data:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to fetch menu data'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setShouldLoadData(true);
    } else {
      setShouldLoadData(false);
      setMenuData('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (shouldLoadData) {
      fetchMenuData();
    }
  }, [shouldLoadData, dynamicS3Url]);

  // Format the content as markdown with YAML syntax highlighting
  const formatAsMarkdown = (content: string): string => {
    if (!content) return '';

    // Detect file type and apply appropriate syntax highlighting
    const getLanguage = () => {
      if (dynamicS3Url?.endsWith('.yml') || dynamicS3Url?.endsWith('.yaml'))
        return 'yaml';
      if (dynamicS3Url?.endsWith('.json')) return 'json';
      if (dynamicS3Url?.endsWith('.xml')) return 'xml';
      if (dynamicS3Url?.endsWith('.txt')) return 'text';
      return 'text';
    };

    const language = getLanguage();

    return `\`\`\`${language}
${content}
\`\`\``;
  };

  const handleCopyMenuData = () => {
    if (menuData) {
      copy(menuData);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted={false}
      disableEnforceFocus={true}
      disableAutoFocus={true}
      transitionDuration={{
        enter: 225,
        exit: 195,
      }}
      slotProps={{
        paper: {
          sx: {
            width: '637px',
            p: 0,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            willChange: 'transform',
          },
        },
      }}
    >
      {/* Sticky Header Section */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#FFFFFF',
          px: 2,
          pt: 2,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Close Button and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                '&:hover': { backgroundColor: '#E8EBF1' },
                transition: 'none',
              }}
            >
              <X size={18} />
            </IconButton>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: 'custom.textBlue',
                letterSpacing: '-0.2px',
              }}
            >
              Agent Menu
            </Typography>
          </Box>

          {/* Status Indicator and Copy Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Copy Button - only show when there's data to copy */}
            {menuData && !isLoading && !error && (
              <IconButton
                onClick={handleCopyMenuData}
                size="small"
                sx={{
                  '&:hover': { backgroundColor: '#E8EBF1' },
                  transition: 'none',
                  color: copied ? 'success.main' : 'text.secondary',
                }}
                title={copied ? 'Copied!' : 'Copy menu data'}
              >
                <Copy size={18} />
              </IconButton>
            )}

            <Chip
              label={
                !shouldLoadData
                  ? 'Ready'
                  : isLoading
                    ? 'Loading...'
                    : error
                      ? 'Error'
                      : copied
                        ? 'Copied!'
                        : 'Loaded'
              }
              size="small"
              color={
                !shouldLoadData
                  ? 'default'
                  : isLoading
                    ? 'default'
                    : error
                      ? 'error'
                      : copied
                        ? 'success'
                        : 'success'
              }
              variant="outlined"
            />
          </Box>
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.08)',
            mb: 2,
            mx: -2,
          }}
        />
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          overflow: 'auto',
          flexGrow: 1,
          px: 2,
          pb: 2,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {/* Loading State */}
        {(isLoading || !shouldLoadData) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            {shouldLoadData && <CircularProgress size={40} sx={{ mb: 2 }} />}
            <Typography variant="body2" color="text.secondary">
              {!shouldLoadData ? 'Preparing menu...' : 'Loading menu data...'}
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !isLoading && shouldLoadData && (
          <Alert
            severity="error"
            sx={{
              borderRadius: '8px',
              mb: 2,
            }}
          >
            <Typography variant="body2">{error}</Typography>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Note: The S3 file must be publicly accessible for direct fetching.
            </Typography>
          </Alert>
        )}

        {/* Menu Content in Markdown Editor - Only render when data is ready */}
        {!isLoading && !error && menuData && shouldLoadData && (
          <Box sx={{ mt: 1 }}>
            <MDEditor
              value={formatAsMarkdown(menuData)}
              preview="preview"
              hideToolbar={true}
              visibleDragbar={false}
              data-color-mode="light"
              height={`calc(100vh - 100px)`}
              style={{
                backgroundColor: 'transparent',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '14px',
              }}
            />
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && !error && !menuData && shouldLoadData && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
            }}
          >
            <ForkKnifeIcon size={48} color="#E0E0E0" />
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                mt: 2,
                mb: 1,
              }}
            >
              No Menu Data Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The menu file appears to be empty.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Optimized custom styles for the markdown editor */}
      <style>{`
        .w-md-editor {
          border: none !important;
          box-shadow: none !important;
          font-family: 'DM Sans', sans-serif !important;
          font-size: 14px !important;
          padding: 0 !important;
        }
        .w-md-editor-text-input,
        .w-md-editor-text-container,
        .w-md-editor-text,
        .w-md-editor-preview,
        .wmde-markdown {
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
          font-size: 14px !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          contain: layout style paint;
        }
        .w-md-editor-preview h1,
        .wmde-markdown h1 {
          font-weight: 600 !important;
          color: #1976d2 !important;
          margin: 0.8em 0 0.4em 0 !important;
          font-size: 18px !important;
        }
        .w-md-editor-preview h2,
        .wmde-markdown h2 {
          font-weight: 500 !important;
          color: #374151 !important;
          margin: 0.6em 0 0.3em 0 !important;
          font-size: 16px !important;
        }
        .w-md-editor-preview pre,
        .wmde-markdown pre {
          background-color: #f8f9fa !important;
          border: 1px solid #e9ecef !important;
          border-radius: 6px !important;
          padding: 1rem !important;
          margin: 1rem 0 !important;
          overflow-x: auto !important;
        }
        .w-md-editor-preview code,
        .wmde-markdown code {
          font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace !important;
          font-size: 13px !important;
        }
        .w-md-editor-preview .token.key,
        .wmde-markdown .token.key {
          color: #d73a49 !important;
        }
        .w-md-editor-preview .token.string,
        .wmde-markdown .token.string {
          color: #032f62 !important;
        }
        .w-md-editor-preview .token.number,
        .wmde-markdown .token.number {
          color: #005cc5 !important;
        }
      `}</style>
    </Drawer>
  );
};

export default MenuDrawer;
